import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Group, Stack } from "@mantine/core";

import { format } from "date-fns";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterNoteDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterNoteDifference";
import { EncounterSummaryDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterSummaryDifference";
import DxDiagnosesVersionDiff from "@/encounters/components/DxPlan/DxDiagnosesVersionDiff";
import EncounterProceduresDiff from "@/encounters/components/EncounterProceduresDiff";
import ProceduresAmendmentDiff from "@/encounters/components/ProceduresAmendmentDiff";
import { FilesDiff } from "@/encounters/components/shared/Files";
import { NewbornIPPEncounterQuestionsLinkIds } from "@/encounters/constants/newborn-ipp";
import { useEncounterDetailVersionPair } from "@/encounters/hooks/useEncounterDetailVersionPair";
import { useLoadBirthDateTime } from "@/encounters/hooks/useLoadBirthDateTime";
import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";
import NewbornSystemsDiff from "@/newborn-systems/NewbornSystemsDiff";
import NotFound from "@/NotFound";
import PhysicalExamsDiff from "@/physical-exams/PhysicalExamsDiff";

import BirthDetailsDiff from "./components/BirthDetailsDiff";
import FeedingDiff from "./components/FeedingDiff";
import GestationalAgeDiff from "./components/GestationalAgeDiff";
import MedicationAndTherapiesDiff from "./components/MedicationAndTherapiesDiff";
import NewbornIPPDiffHeader from "./components/NewbornIPPDiffHeader";
import NewbornIPPEncounterDiff from "./components/NewbornIPPEncounterDiff";
import SizeDiff from "./components/SizeDiff";
import VitalsSignDiff from "./components/VitalsSignDiff";

const VersionDiff = () => {
  const { encounterId, versionId, clientId } = useParams<{
    encounterId: string;
    versionId: string;
    clientId: string;
  }>();

  const [encounter, setEncounter] = useState<Encounter>();
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();
  const [amendmentHistory, setAmendmentHistory] = useState<QuestionnaireResponse[]>();
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(0);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const navigate = useNavigate();
  const medplum = useMedplum();

  useLoadBirthDateTime(clientId);

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        if (!versionId || !encounterId) return;

        const encounter = await medplum.readResource("Encounter", encounterId);
        if (!encounter) return;

        setEncounter(encounter);

        const foundAmendmentHistory = await fetchAmendmentHistory(medplum, encounter);

        if (!foundAmendmentHistory) return;
        const currentHistoryVersionIndex = foundAmendmentHistory.findIndex(
          (questionnaireResponse) => questionnaireResponse.meta?.versionId === versionId,
        );

        if (currentHistoryVersionIndex === -1) return;

        setCurrentVersionIndex(currentHistoryVersionIndex);
        setCurrentVersion(foundAmendmentHistory[currentHistoryVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentHistoryVersionIndex + 1]);
        setAmendmentHistory(foundAmendmentHistory);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [versionId, encounterId, medplum]);

  const {
    currentEncounter,
    previousEncounter,
    isLoading: isEncounterPairLoading,
  } = useEncounterDetailVersionPair(encounterId, amendmentHistory, currentVersionIndex);

  if (isFetchingInitialData || !encounter || isEncounterPairLoading) return <Loading />;

  if ((!currentVersion || !previousVersion) && !isFetchingInitialData) return <NotFound />;

  const lastAmendedBy = currentVersion?.meta?.author?.display;
  const lastAmended = currentVersion?.meta?.lastUpdated;

  return (
    <Stack py="md">
      <NewbornIPPDiffHeader questionnaireResponse={currentVersion!} />
      <NewbornIPPEncounterDiff
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        currentEncounter={currentEncounter}
        previousEncounter={previousEncounter}
      />
      <DxDiagnosesVersionDiff currentVersion={currentVersion} previousVersion={previousVersion} />
      <EncounterNoteDifference
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        questionLinkId={NewbornIPPEncounterQuestionsLinkIds.EncounterNote}
      />
      <EncounterSummaryDifference
        currentVersion={currentVersion}
        previousVersion={previousVersion}
      />
      <BirthDetailsDiff currentVersionIndex={currentVersionIndex} />
      <SizeDiff currentVersionIndex={currentVersionIndex} />
      <PhysicalExamsDiff
        encounter={encounter}
        versionId={versionId}
        currentVersionIndex={currentVersionIndex}
      />
      <VitalsSignDiff currentVersionIndex={currentVersionIndex} />
      <FeedingDiff currentVersionIndex={currentVersionIndex} />
      <MedicationAndTherapiesDiff currentVersionIndex={currentVersionIndex} />
      <GestationalAgeDiff currentVersionIndex={currentVersionIndex} />
      <NewbornSystemsDiff versionId={versionId} currentVersionIndex={currentVersionIndex} />
      <EncounterProceduresDiff encounter={encounter} />
      <ProceduresAmendmentDiff encounter={encounter} versionId={versionId} />
      <FilesDiff currentVersionIndex={currentVersionIndex} />
      <Group justify="flex-end">
        <Stack align="flex-end">
          <Button
            variant="outline"
            data-testid="exit-amendment-view-button"
            onClick={() =>
              navigate(`/clients/${clientId}/encounters/${encounterId}`, {
                preventScrollReset: true,
              })
            }
          >
            Exit Amendment View
          </Button>
          {lastAmendedBy && lastAmended && (
            <TextWrapper>
              Last amended by {lastAmendedBy} - {format(lastAmended, "MM/dd/yyyy")}
            </TextWrapper>
          )}
        </Stack>
      </Group>
    </Stack>
  );
};

export default VersionDiff;
